import type { ApiOkBase } from '../types/api';
import type { GrainCampaign } from '../types/grain';
import { apiFetchJson } from './authed-api';

interface AdminGrainBalanceResponse extends ApiOkBase {
  items?: GrainCampaign[];
  pendingByGrano?: Record<string, number>;
  error?: string;
  code?: string;
}

export class AdminGrainBalanceError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'AdminGrainBalanceError';
    this.code = code;
  }
}

export interface AdminGrainBalanceResult {
  items: GrainCampaign[];
  pendingByGrano: Record<string, number>;
}

export async function fetchAdminGrainBalance(
  accountId: string,
  sessionId: string
): Promise<AdminGrainBalanceResult> {
  void sessionId;
  const result = await apiFetchJson<AdminGrainBalanceResponse>(
    '/api/admin/grain-balance/' + encodeURIComponent(accountId),
    { auth: 'dashboard' }
  );
  if (!result.ok || !result.data.ok) {
    throw new AdminGrainBalanceError(
      result.data.error || 'No se pudo obtener la disponibilidad de granos.',
      result.data.code
    );
  }
  return {
    items: result.data.items ?? [],
    pendingByGrano: result.data.pendingByGrano ?? {},
  };
}

export async function fetchPendingReservationsByGrano(
  accountId: string
): Promise<Record<string, number>> {
  const result = await apiFetchJson<{
    ok?: boolean;
    pendingByGrano?: Record<string, number>;
    error?: string;
  }>('/api/accounts/' + encodeURIComponent(accountId) + '/pending-reservations', {
    auth: 'none',
  });
  if (!result.ok || !result.data.ok) {
    throw new Error(result.data.error || 'No se pudo obtener reservas pendientes.');
  }
  return result.data.pendingByGrano ?? {};
}

/**
 * Iframe app (sin JWT del padre): saldo vía servicio en el mismo origen.
 * Usa accountId de la URL que la app ya envía al abrir el iframe.
 */
export async function fetchEmbedTrustedGrainBalance(
  accountId: string,
  embedToken: string
): Promise<AdminGrainBalanceResult> {
  const qs = new URLSearchParams({ embed: '1', embedToken: embedToken.trim() });
  const result = await apiFetchJson<AdminGrainBalanceResponse>(
    '/api/embed/grain-balance/' + encodeURIComponent(accountId) + '?' + qs.toString(),
    { auth: 'none', timeoutMs: 40_000 }
  );
  if (!result.ok || !result.data.ok) {
    throw new AdminGrainBalanceError(
      result.data.error || 'No se pudo obtener la disponibilidad de granos.',
      result.data.code
    );
  }
  return {
    items: result.data.items ?? [],
    pendingByGrano: result.data.pendingByGrano ?? {},
  };
}

/** Iframe app móvil: mismo origen, token del socio, reservas incluidas (sin CORS a api-agricola). */
export async function fetchAppEmbedGrainBalance(
  accountId: string,
  appToken: string
): Promise<AdminGrainBalanceResult> {
  const result = await apiFetchJson<AdminGrainBalanceResponse>(
    '/api/app/grain-balance/' + encodeURIComponent(accountId),
    {
      auth: 'none',
      timeoutMs: 40_000,
      headers: { Authorization: 'Bearer ' + appToken },
    }
  );
  if (!result.ok || !result.data.ok) {
    const code = result.data.code;
    const message = result.data.error || 'No se pudo obtener la disponibilidad de granos.';
    throw new AdminGrainBalanceError(message, code);
  }
  return {
    items: result.data.items ?? [],
    pendingByGrano: result.data.pendingByGrano ?? {},
  };
}

export async function fetchClientGrainBalance(
  apiBase: string,
  accountId: string,
  token: string
): Promise<GrainCampaign[]> {
  const url = apiBase + '/accounts/' + encodeURIComponent(accountId) + '/grain/balance';
  const response = await fetch(url, {
    headers: {
      Authorization: 'Bearer ' + token,
      Accept: 'application/json',
    },
  });

  if (response.status === 401) {
    throw new Error('Sesión expirada. Volvé a abrir desde la app.');
  }
  if (!response.ok) {
    const body = await response.text();
    throw new Error(
      'No se pudo obtener la disponibilidad de granos (' +
        response.status +
        '). ' +
        (body ? body.slice(0, 200) : '')
    );
  }

  const data = (await response.json()) as unknown;
  return Array.isArray(data) ? (data as GrainCampaign[]) : [];
}
